/**
 * client.ts — the ONE seam switch (B0.1'; fullsuite plan §B0.1', risk #10).
 *
 * Interim triple seam: MSW (mock) · gold REST (`/api` via the vite proxy) ·
 * GraphQL (`/graphql` via the vite proxy — targets live in vite.config.ts,
 * never here). `VITE_LIVE_MODULES` is the ONLY per-module flip: a
 * comma-separated list of module keys whose data traffic leaves the mock seam
 * and rides the live GraphQL server. No other env var, flag or code path may
 * route FE data traffic between seams — `yarn seam:audit`
 * (scripts/seam-audit.mjs) enforces this by grep, and this file is the only
 * sanctioned read site of the variable.
 *
 * Consumers arrive with the CU flips (B0.6' parity harness → CU-0 auth at
 * ~week 4–5). Until then the set is empty and every module stays on today's
 * seam; the gold REST seam dies at CU-2, the mock seam at B7.4'.
 *
 * B1.3' — the first two module keys are WIRED (consumers exist):
 *   · `auth`       → REST-by-adoption on the GraphQL server (auth.api.ts;
 *                    the vite proxy routes `/api/auth` there and the MSW
 *                    worker passes the prefix through when this key is live).
 *   · `governance` → GraphQL via the B0.7' contract operations
 *                    (governance.api.ts → graphql.client.ts → `/graphql`).
 * B2.6' adds:
 *   · `masters`    → GraphQL page-operation READS for vendor / customer /
 *                    master-data contact / flat party category
 *                    ({vendor,customer,masterDataContact,category}.api.ts →
 *                    masters.operations.ts → `/graphql`).
 * CU-1a (the masters flip) grows the same key to the WRITE path and the
 * reference roots: vendor create/check-exists, vendor contact + bank
 * satellites (contact.api.ts / bank.api.ts), customer check-exists, flat
 * party-category create/toggle, currencies (currency.api.ts), plus
 * search/categoryIds/isActive filter plumbing on the list reads. The
 * remaining masters legs (updates/toggles, locations, payment, masters
 * sort/cursor variables) ride the mock seam pending the B2.7' completion
 * pass — register-listed.
 * Unknown keys remain inert by design (no consumer reads them).
 *
 * Two CONFIG-PLANE mirrors of this switch exist outside src/ and must agree
 * with the runtime read here (both documented at their sites):
 * vite.config.ts (dev-proxy routing for `/api/auth`, evaluated at dev-server
 * boot) and mocks/handlers/index.ts (MSW passthrough — which IMPORTS
 * `isLiveModule` from this file, so the runtime read site stays single).
 */

/** Module keys with live wiring landed (B1.3' + B2.6' + CU-1b). Grows at each CU flip. */
export const WIRED_LIVE_MODULE_KEYS = ['auth', 'governance', 'masters', 'taxonomy', 'pricing', 'products'] as const;

declare global {
  interface ImportMetaEnv {
    /** Comma-separated module keys served by the live GraphQL server. */
    readonly VITE_LIVE_MODULES?: string;
  }
}

/** Pure CSV → key-set parse (exported for the seam vitest matrix). */
export function parseLiveModules(raw: string | undefined): ReadonlySet<string> {
  return new Set(
    (raw ?? '')
      .split(',')
      .map((key) => key.trim())
      .filter(Boolean)
  );
}

/** Module keys currently flipped to the live GraphQL seam. Empty = all-mock. */
export const liveModules: ReadonlySet<string> = parseLiveModules(
  import.meta.env.VITE_LIVE_MODULES
);

/** True when `moduleKey` (e.g. 'governance.core') is served by the live graph. */
export function isLiveModule(moduleKey: string): boolean {
  return liveModules.has(moduleKey);
}
